import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, ChevronDown, ChevronUp, RefreshCw,
  Settings2, Pencil, Play, Pause, FlaskConical, Radio, X
} from "lucide-react";
import { Combobox } from "@/components/ui/combobox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import MorningTea from "./MorningTea";

const EXCHANGES = ["NSE", "MCX"];

// ═══════════════════════════════════════════════════════════════
// ── Shared UI Primitives ──────────────────────────────────────
// ═══════════════════════════════════════════════════════════════

function Field({ label, tip, required, children, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-xs font-medium text-zinc-400 tracking-wide">
        {label}
        {required && <span className="text-rose-400 ml-0.5">*</span>}
        {tip && (
          <span className="group relative ml-1.5 inline-block align-middle">
            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/5 text-white/30 text-[9px] cursor-help border border-white/10">?</span>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block bg-zinc-800 text-white/80 text-[10px] px-2.5 py-1.5 rounded-md whitespace-nowrap z-50 border border-white/10 shadow-xl max-w-[240px] text-wrap leading-relaxed">
              {tip}
            </span>
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, step = 0.1, min, id, placeholder }) {
  return (
    <input
      data-testid={id}
      type="number"
      step={step}
      min={min}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
      placeholder={placeholder}
      className="w-full bg-zinc-950/60 border border-zinc-700/50 focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/20 rounded-md text-white text-sm px-3 py-2.5 placeholder:text-zinc-600 font-mono outline-none transition-all"
    />
  );
}

function NumField({ label, value, onChange, step = 0.1, min, tip, id, placeholder }) {
  return (
    <Field label={label} tip={tip}>
      <NumInput value={value} onChange={onChange} step={step} min={min} id={id} placeholder={placeholder} />
    </Field>
  );
}

function RadioGroup({ label, name, value, options, onChange, id }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-5">
        {options.map((opt) => (
          <label
            key={opt.value}
            className={`flex items-center gap-2 cursor-pointer text-sm transition-colors ${
              value === opt.value ? "text-white" : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <input type="radio" name={name} value={opt.value} checked={value === opt.value}
              onChange={() => onChange(opt.value)} data-testid={`${id}-${opt.value}`} className="accent-cyan-500" />
            {opt.label}
          </label>
        ))}
      </div>
    </Field>
  );
}

function YesNoToggle({ label, value, onChange, id }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-4">
        <label className={`flex items-center gap-2 cursor-pointer text-sm ${value ? "text-emerald-400" : "text-zinc-500"}`}>
          <input type="radio" checked={value === true} onChange={() => onChange(true)} className="accent-emerald-500" data-testid={`${id}-yes`} /> Yes
        </label>
        <label className={`flex items-center gap-2 cursor-pointer text-sm ${!value ? "text-rose-400" : "text-zinc-500"}`}>
          <input type="radio" checked={value === false} onChange={() => onChange(false)} className="accent-rose-500" data-testid={`${id}-no`} /> No
        </label>
      </div>
    </Field>
  );
}

function Section({ title, number, children, actions }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-zinc-900/80">
        <div className="flex items-center gap-2.5">
          {number && (
            <span className="flex items-center justify-center w-5 h-5 rounded bg-cyan-500/15 text-cyan-400 text-[10px] font-mono font-bold">{number}</span>
          )}
          <h3 className="text-sm font-semibold text-zinc-200 tracking-wide">{title}</h3>
        </div>
        {actions}
      </div>
      <div className="p-5 space-y-5">{children}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ── Traffic Light Status Indicator ────────────────────────────
// ═══════════════════════════════════════════════════════════════

function TrafficLight({ mode, status }) {
  // Green = LIVE + ACTIVE, Yellow = FORWARD_TEST + ACTIVE, Red = PAUSED
  const isGreen = status === "ACTIVE" && mode === "LIVE";
  const isYellow = status === "ACTIVE" && mode === "FORWARD_TEST";
  const isRed = status === "PAUSED";

  const label = isGreen ? "Live" : isYellow ? "Testing" : "Paused";

  return (
    <div className="flex items-center gap-2" data-testid="strategy-traffic-light">
      <div className="flex items-center gap-1 bg-zinc-950 rounded-full px-2 py-1 border border-zinc-800">
        <span className={`w-2.5 h-2.5 rounded-full transition-all ${isRed ? "bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.6)]" : "bg-zinc-700"}`} />
        <span className={`w-2.5 h-2.5 rounded-full transition-all ${isYellow ? "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.6)]" : "bg-zinc-700"}`} />
        <span className={`w-2.5 h-2.5 rounded-full transition-all ${isGreen ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]" : "bg-zinc-700"}`} />
      </div>
      <span className={`text-[10px] font-mono font-semibold ${isGreen ? "text-emerald-400" : isYellow ? "text-amber-400" : "text-rose-400"}`}>
        {label}
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ── Default values ────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════

const EMPTY = {
  name: "", symbol: "", exchange: "NSE", mode: "FORWARD_TEST", status: "ACTIVE",
  // Seed data (key levels)
  previous_top: null, previous_bottom: null,
  prev_month_high: null, prev_month_low: null,
  monthly_trend: "NEUTRAL",
  // Position Sizing
  monthly_trend_buffer_pct: 2.5, trended_lots: 2, contra_lots: 1,
  rm1_max_loss_pct: 3.0, rm2_first_day_loss_pct: 1.5, rm3_itm_pct: 3.0,
  rm4_gap_pct: 3.0, rm5_pct: 2.0,
  rm6_time_buffer_min: 5, rm6_points_buffer: 0.05,
  rm7_time_buffer_min: 15, rm7_points_buffer: 0.05,
  entry_buffer_pct: 0.2, exit_buffer_pct: 0.2,
  cooldown_minutes: 1, rollover_days_before_expiry: 1, rollover_time: "14:00",
  order_execution_type: "MARKET", smart_limit_buffer_pts: 2.0,
  smart_limit_timeout_sec: 5, smart_limit_step_pts: 1.0,
  pm_enabled: true, pm1_target_pct: 3.0, pm2_target_pct: 6.0,
  pm1_exit_lots: 1, pm2_exit_lots: 1,
  target_booking_mode: "PARTIAL", re_entry_mode: "NEXT_HOUR",
  mt_bullish_long: true, mt_bullish_short: true,
  mt_bearish_short: true, mt_bearish_long: true,
  tbr_enabled: false, tbr_ee1_pct: 1.0, tbr_ee2_upper_limit: 1.0,
  tbr_ee2_long_buffer: 1.0, tbr_ee2_short_buffer: 1.0,
  tbr_ee3_pct: 1.0, tbr_ee3_short_buffer: 1.0,
};

// ═══════════════════════════════════════════════════════════════
// ── Strategy Card (List Item) ─────────────────────────────────
// ═══════════════════════════════════════════════════════════════

function StrategyCard({ strategy, onEdit, onDelete, onToggleStatus }) {
  const s = strategy;
  return (
    <div
      data-testid={`strategy-card-${s.symbol}`}
      className="flex items-center justify-between px-5 py-4 rounded-lg border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-900/80 transition-all"
    >
      <div className="flex items-center gap-4">
        <TrafficLight mode={s.mode} status={s.status} />
        <div>
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold text-sm">{s.name || "Unnamed"}</span>
            <span className="text-zinc-500 text-xs font-mono">{s.symbol}</span>
            <span className="text-zinc-600 text-xs font-mono">{s.exchange}</span>
          </div>
          <div className="flex items-center gap-3 mt-1">
            <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${
              s.mode === "LIVE"
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                : "bg-amber-500/10 text-amber-400 border-amber-500/20"
            }`}>
              {s.mode === "LIVE" ? <Radio className="w-2.5 h-2.5" /> : <FlaskConical className="w-2.5 h-2.5" />}
              {s.mode === "LIVE" ? "Live" : "Forward Test"}
            </span>
            {s.pm_enabled && (
              <span className="text-[10px] text-zinc-500 font-mono">PM: {s.pm1_target_pct}% / {s.pm2_target_pct}%</span>
            )}
            <span className="text-[10px] text-zinc-600 font-mono">Lots: {s.trended_lots}T / {s.contra_lots}C</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          data-testid={`strategy-toggle-status-${s.symbol}`}
          onClick={() => onToggleStatus(s)}
          title={s.status === "ACTIVE" ? "Pause strategy" : "Activate strategy"}
          className={`p-2 rounded-md transition-all ${
            s.status === "ACTIVE"
              ? "text-amber-400 hover:bg-amber-500/10"
              : "text-emerald-400 hover:bg-emerald-500/10"
          }`}
        >
          {s.status === "ACTIVE" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button
          data-testid={`strategy-edit-${s.symbol}`}
          onClick={() => onEdit(s)}
          title="Edit strategy"
          className="p-2 rounded-md text-zinc-400 hover:text-white hover:bg-white/5 transition-all"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          data-testid={`strategy-delete-${s.symbol}`}
          onClick={() => onDelete(s.id)}
          title="Delete strategy"
          className="p-2 rounded-md text-zinc-600 hover:text-rose-400 hover:bg-rose-500/5 transition-all"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ── Strategy Form (Create / Edit) ─────────────────────────────
// ═══════════════════════════════════════════════════════════════

function StrategyForm({ strategy, onSave, onCancel, isNew = false }) {
  const [f, setF] = useState(strategy);
  const [saving, setSaving] = useState(false);
  const [futuresSymbols, setFuturesSymbols] = useState([]);
  const [loadingSymbols, setLoadingSymbols] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const fetchFuturesSymbols = useCallback(async (exchange) => {
    setLoadingSymbols(true);
    try {
      const response = await api.get("/futures-symbols", { params: { exchange } });
      if (response.data.status && response.data.symbols) {
        const symbols = response.data.symbols
          .filter(s => !s.includes('TEST') && !s.includes('NSETEST'))
          .map(s => ({ value: s, label: s }));
        setFuturesSymbols(symbols);
      }
    } catch {
      setFuturesSymbols([]);
    } finally {
      setLoadingSymbols(false);
    }
  }, []);

  useEffect(() => {
    if (isNew) fetchFuturesSymbols(f.exchange);
  }, [f.exchange, isNew, fetchFuturesSymbols]);

  const handleSave = async () => {
    if (!f.name?.trim()) return toast.error("Strategy Name is required");
    if (!f.symbol?.trim()) return toast.error("Symbol is required");
    setSaving(true);
    try {
      await onSave(f);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="strategy-form">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">
          {isNew ? "Create New Strategy" : `Edit: ${f.name || f.symbol}`}
        </h2>
        <button onClick={onCancel} className="p-2 text-zinc-500 hover:text-white rounded-md hover:bg-white/5 transition-all">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* ─── 1. Strategy Identity ─── */}
      <Section title="Strategy Identity" number="1">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Field label="Strategy Name" required className="lg:col-span-2">
            <input data-testid="strategy-name-input" type="text" value={f.name || ""}
              onChange={(e) => set("name", e.target.value)} placeholder="e.g. NIFTY-Swing-Hourly"
              className="w-full bg-zinc-950/60 border border-zinc-700/50 focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/20 rounded-md text-white text-sm px-3 py-2.5 placeholder:text-zinc-600 outline-none transition-all" />
          </Field>
          {isNew ? (
            <>
              <Field label="Exchange">
                <Select value={f.exchange} onValueChange={(v) => { set("exchange", v); set("symbol", ""); }}>
                  <SelectTrigger data-testid="strategy-exchange" className="bg-zinc-950/60 border-zinc-700/50 focus:border-cyan-500/60 rounded-md text-white text-sm h-[42px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {EXCHANGES.map((e) => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Symbol" required>
                <Combobox options={futuresSymbols} value={f.symbol}
                  onChange={(v) => set("symbol", v)}
                  placeholder={loadingSymbols ? "Loading..." : "Search symbol..."}
                  searchPlaceholder="Type to search (e.g., NIFTY, POLYCAB)..."
                  emptyText="No symbol found" disabled={loadingSymbols}
                  data-testid="strategy-symbol-input" />
              </Field>
            </>
          ) : (
            <>
              <Field label="Symbol">
                <div className="flex items-center h-[42px] px-3 bg-zinc-950/40 border border-zinc-800 rounded-md text-white text-sm font-mono">
                  {f.symbol}
                </div>
              </Field>
              <Field label="Exchange">
                <div className="flex items-center h-[42px] px-3 bg-zinc-950/40 border border-zinc-800 rounded-md text-white text-sm font-mono">
                  {f.exchange}
                </div>
              </Field>
            </>
          )}
        </div>
        {/* Mode & Status */}
        <div className="pt-3 border-t border-zinc-800/50 flex flex-wrap items-end gap-6">
          <Field label="Mode" tip="Forward Test = signals only. Live = signals + trade execution.">
            <div className="flex items-center gap-1 p-1 bg-zinc-950/60 rounded-md border border-zinc-700/50 w-fit">
              <button type="button" data-testid="mode-forward-test" onClick={() => set("mode", "FORWARD_TEST")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  f.mode === "FORWARD_TEST" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "text-zinc-500 hover:text-zinc-300"
                }`}>
                <FlaskConical className="w-3.5 h-3.5" /> Forward Test
              </button>
              <button type="button" data-testid="mode-live" onClick={() => set("mode", "LIVE")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  f.mode === "LIVE" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "text-zinc-500 hover:text-zinc-300"
                }`}>
                <Radio className="w-3.5 h-3.5" /> Live
              </button>
            </div>
          </Field>
          <Field label="Status">
            <div className="flex items-center gap-1 p-1 bg-zinc-950/60 rounded-md border border-zinc-700/50 w-fit">
              <button type="button" data-testid="status-active" onClick={() => set("status", "ACTIVE")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  f.status === "ACTIVE" ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" : "text-zinc-500 hover:text-zinc-300"
                }`}>
                <Play className="w-3.5 h-3.5" /> Active
              </button>
              <button type="button" data-testid="status-paused" onClick={() => set("status", "PAUSED")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  f.status === "PAUSED" ? "bg-rose-500/20 text-rose-400 border border-rose-500/30" : "text-zinc-500 hover:text-zinc-300"
                }`}>
                <Pause className="w-3.5 h-3.5" /> Paused
              </button>
            </div>
          </Field>
          <div className="ml-auto">
            <TrafficLight mode={f.mode} status={f.status} />
          </div>
        </div>
      </Section>

      {/* ─── 2. Key Levels (Seed Data) ─── */}
      <Section title="Key Levels (Seed Data)" number="2">
        <p className="text-xs text-zinc-500 -mt-2 mb-3">Initial values — the engine updates Monthly Trend automatically based on CMP.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-5">
          <NumField label="Previous Top (PT)" value={f.previous_top} onChange={(v) => set("previous_top", v)}
            step={0.05} tip="Last swing high — used for Long Entry and Short Exit" id="seed-pt" placeholder="Required" />
          <NumField label="Previous Bottom (PB)" value={f.previous_bottom} onChange={(v) => set("previous_bottom", v)}
            step={0.05} tip="Last swing low — used for Short Entry and Long Exit" id="seed-pb" placeholder="Required" />
          <NumField label="Prev Month High" value={f.prev_month_high} onChange={(v) => set("prev_month_high", v)}
            step={0.05} tip="Used with buffer to determine Bullish threshold" id="seed-pm-high" />
          <NumField label="Prev Month Low" value={f.prev_month_low} onChange={(v) => set("prev_month_low", v)}
            step={0.05} tip="Used with buffer to determine Bearish threshold" id="seed-pm-low" />
          <Field label="Monthly Trend" tip="Current trend state — flips when CMP crosses PM thresholds">
            <div className="flex items-center gap-1 p-1 bg-zinc-950/60 rounded-md border border-zinc-700/50 w-fit">
              <button type="button" data-testid="trend-bullish" onClick={() => set("monthly_trend", "BULLISH")}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  f.monthly_trend === "BULLISH" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "text-zinc-500 hover:text-zinc-300"
                }`}>Bullish</button>
              <button type="button" data-testid="trend-bearish" onClick={() => set("monthly_trend", "BEARISH")}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  f.monthly_trend === "BEARISH" ? "bg-rose-500/20 text-rose-400 border border-rose-500/30" : "text-zinc-500 hover:text-zinc-300"
                }`}>Bearish</button>
              <button type="button" data-testid="trend-neutral" onClick={() => set("monthly_trend", "NEUTRAL")}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  f.monthly_trend === "NEUTRAL" ? "bg-zinc-700/50 text-zinc-300 border border-zinc-600" : "text-zinc-500 hover:text-zinc-300"
                }`}>Neutral</button>
            </div>
          </Field>
        </div>
      </Section>

      {/* ─── 3. Position Sizing ─── */}
      <Section title="Position Sizing" number="3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <NumField label="Monthly Trend Buffer (%)" value={f.monthly_trend_buffer_pct} onChange={(v) => set("monthly_trend_buffer_pct", v)}
            tip="Buffer % added to prev month high/low to determine monthly trend" id="monthly-trend-buffer" />
          <NumField label="Trended Quantity (Lots)" value={f.trended_lots} onChange={(v) => set("trended_lots", v)}
            step={1} min={1} tip="Lots when trading WITH the monthly trend" id="trended-lots" />
          <NumField label="Contra Quantity (Lots)" value={f.contra_lots} onChange={(v) => set("contra_lots", v)}
            step={1} min={1} tip="Lots when trading AGAINST the monthly trend" id="contra-lots" />
        </div>
      </Section>

      {/* ─── 4. Risk Management ─── */}
      <Section title="Risk Management" number="4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <NumField label="RM#1 - Max Loss (%)" value={f.rm1_max_loss_pct} onChange={(v) => set("rm1_max_loss_pct", v)}
            tip="Maximum loss % per trade before forced exit" id="rm1-max-loss" />
          <NumField label="RM#2 - 1st Day of Entry (%)" value={f.rm2_first_day_loss_pct} onChange={(v) => set("rm2_first_day_loss_pct", v)}
            tip="Tighter SL on the first day of entry" id="rm2-first-day" />
          <NumField label="RM#3 - In The Money (%)" value={f.rm3_itm_pct} onChange={(v) => set("rm3_itm_pct", v)}
            tip="Move SL to break-even when ITM by this %" id="rm3-itm" />
          <NumField label="RM#4 - Gap Up/Down (%)" value={f.rm4_gap_pct} onChange={(v) => set("rm4_gap_pct", v)}
            tip="If gap exceeds threshold, EXIT only - no new positions" id="rm4-gap" />
          <NumField label="RM#5 - Max Adverse (%)" value={f.rm5_pct} onChange={(v) => set("rm5_pct", v)}
            tip="Exit if CMP moves adversely by more than this % from entry price" id="rm5" />
        </div>
        <div className="pt-4 border-t border-zinc-800/50">
          <p className="text-xs text-zinc-500 mb-4">Gap &amp; Entry Confirmation</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <NumField label="RM#6 Time Buffer (min)" value={f.rm6_time_buffer_min} onChange={(v) => set("rm6_time_buffer_min", v)}
              step={1} min={1} tip="Entry confirmation window: tighter adverse buffer for first N minutes after entry" id="rm6-time" />
            <NumField label="RM#6 Points Buffer" value={f.rm6_points_buffer} onChange={(v) => set("rm6_points_buffer", v)}
              step={0.01} tip="Max adverse points allowed during RM6 window before entry is rejected" id="rm6-points" />
            <NumField label="RM#7 Time Buffer (min)" value={f.rm7_time_buffer_min} onChange={(v) => set("rm7_time_buffer_min", v)}
              step={1} min={1} tip="Wait period after RM#6 before new entry" id="rm7-time" />
            <NumField label="RM#7 Points Buffer" value={f.rm7_points_buffer} onChange={(v) => set("rm7_points_buffer", v)}
              step={0.01} tip="Points buffer for adjusted entry level after gap" id="rm7-points" />
          </div>
        </div>
      </Section>

      {/* ─── 5. TBR ─── */}
      <Section title="Top Bottom Range (TBR)" number="5"
        actions={
          <div className="flex items-center gap-1 p-0.5 bg-zinc-950/60 rounded border border-zinc-700/50">
            <button type="button" onClick={() => set("tbr_enabled", true)} data-testid="tbr-enable"
              className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${f.tbr_enabled ? "bg-cyan-500/20 text-cyan-400" : "text-zinc-500"}`}>
              Enabled
            </button>
            <button type="button" onClick={() => set("tbr_enabled", false)} data-testid="tbr-disable"
              className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${!f.tbr_enabled ? "bg-zinc-700/50 text-zinc-300" : "text-zinc-500"}`}>
              Disabled
            </button>
          </div>
        }>
        {f.tbr_enabled ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <NumField label="EE1 - Entry Exception 1 (%)" value={f.tbr_ee1_pct} onChange={(v) => set("tbr_ee1_pct", v)}
              tip="No new entry if TBR < this % of prev close" id="tbr-ee1" />
            <NumField label="EE2 - Upper Limit" value={f.tbr_ee2_upper_limit} onChange={(v) => set("tbr_ee2_upper_limit", v)}
              tip="Upper limit for EE2 modified buffer" id="tbr-ee2-upper" />
            <div />
            <NumField label="EE2 - Long Buffer" value={f.tbr_ee2_long_buffer} onChange={(v) => set("tbr_ee2_long_buffer", v)}
              tip="Modified entry buffer for LONG when EE1 is active" id="tbr-ee2-long" />
            <NumField label="EE2 - Short Buffer" value={f.tbr_ee2_short_buffer} onChange={(v) => set("tbr_ee2_short_buffer", v)}
              tip="Modified entry buffer for SHORT when EE1 is active" id="tbr-ee2-short" />
            <div />
            <NumField label="EE3 - Entry Exception 3 (%)" value={f.tbr_ee3_pct} onChange={(v) => set("tbr_ee3_pct", v)}
              tip="Third entry exception threshold" id="tbr-ee3" />
            <NumField label="EE3 - Short Buffer" value={f.tbr_ee3_short_buffer} onChange={(v) => set("tbr_ee3_short_buffer", v)}
              tip="Modified short buffer for EE3" id="tbr-ee3-short" />
          </div>
        ) : (
          <p className="text-zinc-600 text-xs">TBR is disabled. Enable to configure Entry Exception rules.</p>
        )}
      </Section>

      {/* ─── 6. Profit Management ─── */}
      <Section title="Profit Management" number="6"
        actions={
          <div className="flex items-center gap-1 p-0.5 bg-zinc-950/60 rounded border border-zinc-700/50">
            <button type="button" onClick={() => set("pm_enabled", true)} data-testid="pm-enabled-on"
              className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${f.pm_enabled ? "bg-cyan-500/20 text-cyan-400" : "text-zinc-500"}`}>
              Enabled
            </button>
            <button type="button" onClick={() => set("pm_enabled", false)} data-testid="pm-enabled-off"
              className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${!f.pm_enabled ? "bg-zinc-700/50 text-zinc-300" : "text-zinc-500"}`}>
              Disabled
            </button>
          </div>
        }>
        {f.pm_enabled ? (
          <div className="space-y-5">
            <div>
              <p className="text-xs text-zinc-500 mb-3">Monthly Trend Direction Toggles</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                <YesNoToggle label="MT-Bullish / Long" value={f.mt_bullish_long} onChange={(v) => set("mt_bullish_long", v)} id="mt-bull-long" />
                <YesNoToggle label="MT-Bullish / Short" value={f.mt_bullish_short} onChange={(v) => set("mt_bullish_short", v)} id="mt-bull-short" />
                <YesNoToggle label="MT-Bearish / Short" value={f.mt_bearish_short} onChange={(v) => set("mt_bearish_short", v)} id="mt-bear-short" />
                <YesNoToggle label="MT-Bearish / Long" value={f.mt_bearish_long} onChange={(v) => set("mt_bearish_long", v)} id="mt-bear-long" />
              </div>
            </div>
            <div className="pt-4 border-t border-zinc-800/50">
              <RadioGroup label="Target Profit Booking" name={`booking-${f.id || "new"}`}
                value={f.target_booking_mode}
                options={[
                  { value: "FULL", label: "Full Quantity at Target" },
                  { value: "PARTIAL", label: "Partial Quantity at Various Targets" },
                ]}
                onChange={(v) => set("target_booking_mode", v)} id="booking-mode" />
            </div>
            <div className="pt-4 border-t border-zinc-800/50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                <NumField label="PM#1 Target (%)" value={f.pm1_target_pct} onChange={(v) => set("pm1_target_pct", v)}
                  tip="First profit target % from entry" id="pm1-target" />
                <NumField label="PM#1 Exit Lots" value={f.pm1_exit_lots} onChange={(v) => set("pm1_exit_lots", v)}
                  step={1} min={1} tip="Lots to exit at PM#1" id="pm1-lots" />
                <NumField label="PM#2 Target (%)" value={f.pm2_target_pct} onChange={(v) => set("pm2_target_pct", v)}
                  tip="Second profit target % from entry" id="pm2-target" />
                <NumField label="PM#2 Exit Lots" value={f.pm2_exit_lots} onChange={(v) => set("pm2_exit_lots", v)}
                  step={1} min={1} tip="Lots to exit at PM#2" id="pm2-lots" />
              </div>
            </div>
          </div>
        ) : (
          <p className="text-zinc-600 text-xs">Profit management is disabled.</p>
        )}
      </Section>

      {/* ─── 7. Re-Entry ─── */}
      <Section title="Re-Entry After Target Booking" number="7">
        <RadioGroup label="" name={`reentry-${f.id || "new"}`}
          value={f.re_entry_mode}
          options={[
            { value: "NEXT_HOUR", label: "Entry at Next Hour" },
            { value: "NEXT_DAY", label: "Entry on Next Day" },
          ]}
          onChange={(v) => set("re_entry_mode", v)} id="re-entry" />
      </Section>

      {/* ─── 8. Execution ─── */}
      <Section title="Execution Parameters" number="8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <NumField label="Entry Buffer (%)" value={f.entry_buffer_pct} onChange={(v) => set("entry_buffer_pct", v)}
            tip="Added to Prev Top for Long Entry, subtracted from Prev Bottom for Short Entry" id="entry-buffer" />
          <NumField label="Exit Buffer (%)" value={f.exit_buffer_pct} onChange={(v) => set("exit_buffer_pct", v)}
            tip="Subtracted from Prev Bottom for Long Exit, added to Prev Top for Short Exit" id="exit-buffer" />
          <NumField label="Cool Down Period (min)" value={f.cooldown_minutes} onChange={(v) => set("cooldown_minutes", v)}
            step={1} min={0} tip="Wait time after exit before re-entry" id="cooldown" />
          <NumField label="Rollover Before Expiry (days)" value={f.rollover_days_before_expiry} onChange={(v) => set("rollover_days_before_expiry", v)}
            step={1} min={0} tip="Days before expiry to rollover" id="rollover-days" />
          <Field label="Rollover Time" tip="Time to execute rollover (24h format)">
            <input data-testid="rollover-time" type="time" value={f.rollover_time || "14:00"}
              onChange={(e) => set("rollover_time", e.target.value)}
              className="w-full bg-zinc-950/60 border border-zinc-700/50 focus:border-cyan-500/60 rounded-md text-white text-sm px-3 py-2.5 font-mono outline-none transition-all" />
          </Field>
        </div>
      </Section>

      {/* ─── 9. Order Execution ─── */}
      <Section title="Order Execution" number="9">
        <Field label="Order Type" tip="MARKET = instant fill. SMART_LIMIT = Limit order first, auto-convert to Market if not filled within timeout">
          <div className="flex items-center gap-1 p-1 bg-zinc-950/60 rounded-md border border-zinc-700/50 w-fit">
            <button type="button" data-testid="order-type-market" onClick={() => set("order_execution_type", "MARKET")}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                f.order_execution_type === "MARKET" ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" : "text-zinc-500 hover:text-zinc-300"
              }`}>Market</button>
            <button type="button" data-testid="order-type-smart" onClick={() => set("order_execution_type", "SMART_LIMIT")}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                f.order_execution_type === "SMART_LIMIT" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "text-zinc-500 hover:text-zinc-300"
              }`}>Smart Limit</button>
          </div>
        </Field>
        {f.order_execution_type === "SMART_LIMIT" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-4 p-4 bg-amber-500/5 border border-amber-500/10 rounded-lg">
            <NumField label="Limit Buffer (pts)" value={f.smart_limit_buffer_pts} onChange={(v) => set("smart_limit_buffer_pts", v)}
              step={0.5} tip="Initial offset from desired price for limit order" id="smart-buffer" />
            <NumField label="Timeout (sec)" value={f.smart_limit_timeout_sec} onChange={(v) => set("smart_limit_timeout_sec", v)}
              step={1} min={1} max={30} tip="Seconds to wait before converting to market order" id="smart-timeout" />
            <NumField label="Step Adjust (pts)" value={f.smart_limit_step_pts} onChange={(v) => set("smart_limit_step_pts", v)}
              step={0.5} tip="Price adjustment step every second while waiting" id="smart-step" />
          </div>
        )}
      </Section>

      {/* ─── Actions ─── */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button onClick={onCancel}
          className="px-5 py-2.5 text-sm text-zinc-400 hover:text-white rounded-md hover:bg-white/5 transition-all">
          Cancel
        </button>
        <button data-testid={isNew ? "strategy-create-btn" : `strategy-save-btn`}
          onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-md px-6 py-2.5 text-sm transition-all shadow-[0_0_20px_-5px_rgba(6,182,212,0.4)] hover:shadow-[0_0_25px_-5px_rgba(6,182,212,0.6)] disabled:opacity-50">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : isNew ? <Plus className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {isNew ? "Create Strategy" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ── Main Page ─────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════

export default function Strategies() {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // "list" | "create" | "edit"
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [tab, setTab] = useState(() => localStorage.getItem("strategies_tab") || "rtt");

  const switchTab = (t) => {
    setTab(t);
    localStorage.setItem("strategies_tab", t);
  };

  const fetchAll = useCallback(async () => {
    try {
      const res = await api.get("/strategy/strategies");
      setStrategies(res.data.data || []);
    } catch { toast.error("Failed to load strategies"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async (form) => {
    try {
      await api.post("/strategy/strategies", form);
      toast.success(`Strategy "${form.name}" created for ${form.symbol}`);
      fetchAll();
      setView("list");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create strategy"); }
  };

  const handleUpdate = async (form) => {
    try {
      await api.put(`/strategy/strategies/${form.id}`, form);
      toast.success(`"${form.name}" saved`);
      fetchAll();
      setView("list");
      setEditingStrategy(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this strategy and all its signals?")) return;
    try {
      await api.delete(`/strategy/strategies/${id}`);
      toast.success("Strategy deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const handleToggleStatus = async (strategy) => {
    const newStatus = strategy.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    try {
      await api.put(`/strategy/strategies/${strategy.id}`, { ...strategy, status: newStatus });
      toast.success(`${strategy.name} ${newStatus === "ACTIVE" ? "activated" : "paused"}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update status"); }
  };

  const handleEdit = (strategy) => {
    setEditingStrategy(strategy);
    setView("edit");
  };

  // ── RENDER: Create / Edit form ──
  if (view === "create") {
    return (
      <div className="space-y-6" data-testid="strategy-params-page">
        <StrategyForm strategy={{ ...EMPTY }} onSave={handleCreate} onCancel={() => setView("list")} isNew={true} />
      </div>
    );
  }

  if (view === "edit" && editingStrategy) {
    return (
      <div className="space-y-6" data-testid="strategy-params-page">
        <StrategyForm strategy={editingStrategy} onSave={handleUpdate} onCancel={() => { setView("list"); setEditingStrategy(null); }} />
      </div>
    );
  }

  // ── RENDER: List view ──
  return (
    <div className="space-y-6" data-testid="strategy-params-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-white tracking-tight">
            Strategies
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Manage your trading strategies and signal generation</p>
        </div>
        {tab === "rtt" && (
          <button
            data-testid="create-strategy-btn"
            onClick={() => setView("create")}
            className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-md px-5 py-2.5 text-sm transition-all shadow-[0_0_20px_-5px_rgba(6,182,212,0.4)] hover:shadow-[0_0_25px_-5px_rgba(6,182,212,0.6)]"
          >
            <Plus className="w-4 h-4" />
            Create Strategy
          </button>
        )}
      </div>

      {/* Strategy-type tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        <button
          data-testid="tab-rtt-strategy"
          onClick={() => switchTab("rtt")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${
            tab === "rtt" ? "border-cyan-500 text-cyan-400" : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          RTT Strategy
        </button>
        <button
          data-testid="tab-morning-tea"
          onClick={() => switchTab("mt")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${
            tab === "mt" ? "border-amber-500 text-amber-400" : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          ☕ Morning Tea
        </button>
      </div>

      {tab === "mt" ? (
        <MorningTea />
      ) : loading ? (
        <div className="text-center py-12 text-zinc-500 text-sm">Loading strategies...</div>
      ) : strategies.length === 0 ? (
        <div className="text-center py-16 rounded-lg border border-dashed border-zinc-800">
          <Settings2 className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No strategies configured yet</p>
          <p className="text-zinc-600 text-xs mt-1 mb-4">Create your first strategy to start generating trade signals</p>
          <button onClick={() => setView("create")}
            className="inline-flex items-center gap-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20 rounded-md px-4 py-2 text-sm font-medium transition-all">
            <Plus className="w-4 h-4" /> Create Strategy
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {strategies.map((s) => (
            <StrategyCard key={s.id} strategy={s} onEdit={handleEdit} onDelete={handleDelete} onToggleStatus={handleToggleStatus} />
          ))}
        </div>
      )}
    </div>
  );
}
